import React from 'react';
import { useAuth } from '../context/AuthContext';
import { formatDate, getTransactionTypeLabel } from '../lib/utils';
import { formatCurrency } from '../lib/currency';
import { TrendingUp, TrendingDown, ArrowLeftRight } from 'lucide-react';

const TransactionList = ({ transactions, onSelect, loading }) => {
  const { currency } = useAuth();
  
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-100 rounded w-1/3" />
                <div className="h-3 bg-gray-100 rounded w-1/4" />
              </div>
              <div className="h-5 bg-gray-100 rounded w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <TrendingUp size={24} className="text-gray-400" />
        </div>
        <h3 className="font-medium text-gray-900 mb-1">No transactions yet</h3>
        <p className="text-sm text-gray-500">Start by adding your first transaction</p>
      </div>
    );
  }

  const getIcon = (type) => {
    switch (type) {
      case 'revenue':
        return <TrendingUp size={18} className="text-green-600" />;
      case 'expense':
        return <TrendingDown size={18} className="text-red-600" />;
      case 'transfer':
        return <ArrowLeftRight size={18} className="text-blue-600" />;
      default:
        return <TrendingUp size={18} className="text-gray-600" />;
    }
  };

  const getIconBg = (type) => {
    switch (type) {
      case 'revenue':
        return 'bg-green-50';
      case 'expense':
        return 'bg-red-50';
      case 'transfer':
        return 'bg-blue-50';
      default:
        return 'bg-gray-50';
    }
  };

  return (
    <div className="space-y-2">
      {transactions.map((txn) => {
        // Use display_amount (converted) if available, otherwise use original amount
        const displayAmount = txn.display_amount !== undefined ? txn.display_amount : txn.amount;
        // Use display_currency if available, otherwise fallback to transaction currency or company default
        const displayCurrency = txn.display_currency || txn.currency || currency;
        
        return (
          <div
            key={txn.id}
            onClick={() => onSelect?.(txn)}
            className={`bg-white rounded-lg p-4 flex items-center gap-4 transition-shadow hover:shadow-md ${
              onSelect ? 'cursor-pointer' : ''
            } ${txn.status === 'voided' ? 'opacity-50' : ''}`}
            data-testid="transactions-row"
          >
            {/* Icon */}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIconBg(txn.transaction_type)}`}>
              {getIcon(txn.transaction_type)}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`font-medium ${txn.status === 'voided' ? 'line-through' : ''}`}>
                  {txn.description || getTransactionTypeLabel(txn.transaction_type)}
                </span>
                {txn.status === 'voided' && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">Voided</span>
                )}
              </div>
              <div className="text-sm text-[hsl(var(--muted-ink))] flex items-center gap-2">
                <span>{formatDate(txn.date)}</span>
                {txn.category?.name && (
                  <>
                    <span>•</span>
                    <span>{txn.category.name}</span>
                  </>
                )}
              </div>
            </div>

            {/* Amount */}
            <div className={`text-right numbered font-semibold ${
              txn.transaction_type === 'revenue' ? 'text-green-600' :
              txn.transaction_type === 'expense' ? 'text-red-600' :
              'text-gray-900'
            }`}>
              {txn.transaction_type === 'expense' ? '-' : txn.transaction_type === 'revenue' ? '+' : ''}
              {formatCurrency(displayAmount, displayCurrency)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TransactionList;
